import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { zeroPad, generateFullName, dateToString } from '@/shared/lib';
import { formatPatientAge, FOLLOW_ON_STUDY_TYPE } from '@/entities/study';
import dayjs from 'dayjs';
import _ from 'lodash';
import { t } from 'ttag';

export const NEW_STUDY_TYPE_OPTIONS = [
  {
    label: 'MCT',
    value: 'MCT',
  },
  {
    label: 'MCT PEEK',
    value: 'MCT PEEK',
  },
  {
    label: 'CARDIAC EVENT',
    value: 'CARDIAC EVENT',
  },
  {
    label: 'HOLTER',
    value: 'HOLTER',
  },
  {
    label: 'EXTENDED HOLTER',
    value: 'EXTENDED HOLTER',
  },
  {
    label: FOLLOW_ON_STUDY_TYPE,
    value: FOLLOW_ON_STUDY_TYPE,
  },
];

/**
 * Turns a study record into the value and label pair used by the change-study-type search dropdown. Computes patient full name, gender initial and age from the year difference against the date of birth, maps Holter and Extended Holter studies carrying a follow-on study to the follow-on option and others to their NEW_STUDY_TYPE_OPTIONS entry, formats both dates in the study timezone as MM/DD/YYYY, and builds a label with a double hyphen bullet for missing values.
 *
 * @param item - Study record with its info, patient, type and timezone fields.
 * @returns Option object pairing the study value fields with its rendered label.
 */
export const formattedStudyInfo = (item: any): any => {
  const currentStudyType = !_.isEmpty(item?.info?.followOnStudy)
    ? FOLLOW_ON_STUDY_TYPE
    : item?.studyType;
  const patientName = generateFullName(item?.info?.patient);
  const patientGender = item?.info?.patient?.gender.charAt(0);
  const patientAge =
    (dayjs().format('YYYY') as any) - (dayjs(item?.info?.patient?.dob).format('YYYY') as any);
  return {
    value: {
      patientName,
      patientGender,
      patientAge,
      studyType: !_.isEmpty(item?.info?.followOnStudy)
        ? _.last(NEW_STUDY_TYPE_OPTIONS)
        : _.find(NEW_STUDY_TYPE_OPTIONS, (x) => x.value === item?.studyType),
      studyId: item?.id,
      studyFid: item?.friendlyId,
      startDate: dateToString({
        isoDate: item?.start,
        timezone: item?.timezone,
        format: 'MM/DD/YYYY',
      }),
      endDate: dateToString({
        isoDate: item?.stop,
        timezone: item?.timezone,
        format: 'MM/DD/YYYY',
      }),
      newStudyType: null,
    },
    label: (
      <div className="custom-option">
        <div className="container-top">
          <div className="patientName">
            {`${patientName} (${patientGender} - ${formatPatientAge(patientAge)})`}
          </div>
          <div className="studyType">{currentStudyType}</div>
        </div>
        <div className="container-bottom">
          <div className="dob">
            <div className="name">{t`DOB`}</div>
            <div className="value">
              {item?.info?.patient?.dob === null
                ? DOUBLE_HYPHEN_BULLET
                : dayjs(item?.info?.patient?.dob).format('MM/DD/YYYY')}
            </div>
          </div>

          <div className="border-line" />

          <div className="studyId">
            <div className="name">{t`Study ID`}</div>
            <div className="value">{zeroPad(item?.friendlyId)}</div>
          </div>

          <div className="border-line" />

          <div className="startDate">
            <div className="name">{t`Start Date`}</div>
            <div className="value">
              {item?.start === null
                ? DOUBLE_HYPHEN_BULLET
                : dateToString({
                    isoDate: item?.start,
                    timezone: item?.timezone,
                    format: 'MM/DD/YYYY',
                  })}
            </div>
          </div>

          <div className="border-line" />

          <div className="endDate">
            <div className="name">{t`End Date`}</div>
            <div className="value">
              {item?.stop === null
                ? DOUBLE_HYPHEN_BULLET
                : dateToString({
                    isoDate: item?.stop,
                    timezone: item?.timezone,
                    format: 'MM/DD/YYYY',
                  })}
            </div>
          </div>
        </div>
      </div>
    ),
  };
};
